import type { MaterialNames } from "@/components/material_symbol_names";
import { DOMAIN } from "@/hooks/useCapabilities";
import type { GoNavChordItem } from "@/hooks/useGoNavChordMode";
import type { Path, useNavigate } from "@/router";

export type PrimaryNavTabItem = {
  domain: DOMAIN;
  label: string;
  link: {
    to: Path;
    params?: Record<string, string | undefined>;
  };
  icon: MaterialNames;
  active: boolean;
};

type BuildPrimaryNavTabItemsInput = {
  pathname: string;
  sectionsLink: Path;
  showActivitiesScreen: boolean;
};

/** Explicit go-nav chord keys — must be unique per primary nav tab. */
const GO_NAV_CHORD_KEY_BY_LABEL: Record<string, string> = {
  Home: "h",
  People: "p",
  Sections: "s",
  Facilities: "f",
  Billing: "b",
  Education: "e",
  Activities: "a",
  Events: "n",
  Jobs: "j",
  Photobooth: "o"
};

function isRouteActive(pathname: string, routePath: string) {
  return pathname.startsWith(routePath);
}

export function primaryNavItemSlug(label: string): string {
  return label.toLowerCase().replace(/\s+/g, "-");
}

export function getGoNavChordKey(label: string): string {
  return GO_NAV_CHORD_KEY_BY_LABEL[label] ?? label.charAt(0).toLowerCase();
}

export function buildGoNavChordItems(
  tabItems: PrimaryNavTabItem[],
  domains: DOMAIN[],
  navigate: ReturnType<typeof useNavigate>
): GoNavChordItem[] {
  return tabItems
    .filter((item) => item.domain === DOMAIN.EVERYONE || domains.includes(item.domain))
    .map((item) => ({
      label: item.label,
      chordKey: getGoNavChordKey(item.label),
      dataTestId: `go-nav-${primaryNavItemSlug(item.label)}`,
      onActivate: () => navigate(item.link.to as Path)
    }));
}

export function buildPrimaryNavTabItems({
  pathname,
  sectionsLink,
  showActivitiesScreen
}: BuildPrimaryNavTabItemsInput): PrimaryNavTabItem[] {
  return [
    {
      domain: DOMAIN.EVERYONE,
      label: "Home",
      link: { to: "/home" as Path },
      icon: "home" as MaterialNames,
      active: pathname === "/home" || pathname.startsWith("/home/")
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "People",
      link: { to: "/people" as Path },
      icon: "group" as MaterialNames,
      active: isRouteActive(pathname, "/people")
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Sections",
      link: { to: sectionsLink },
      icon: "domain" as MaterialNames,
      active: isRouteActive(pathname, "/sections")
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Facilities",
      link: { to: "/facilities" as Path },
      icon: "store" as MaterialNames,
      active: isRouteActive(pathname, "/facilities")
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Billing",
      link: { to: "/billing" as Path },
      icon: "card_membership" as MaterialNames,
      active: isRouteActive(pathname, "/billing")
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Education",
      link: { to: "/education" as Path },
      icon: "school" as MaterialNames,
      active: isRouteActive(pathname, "/education")
    },
    ...(showActivitiesScreen
      ? [
          {
            domain: DOMAIN.EVERYONE,
            label: "Activities",
            link: { to: "/activities" as Path },
            icon: "today" as MaterialNames,
            active: isRouteActive(pathname, "/activities")
          }
        ]
      : [
          {
            domain: DOMAIN.EVERYONE,
            label: "Events",
            link: { to: "/events" as Path },
            icon: "today" as MaterialNames,
            active: isRouteActive(pathname, "/events")
          }
        ]),
    {
      domain: DOMAIN.EVERYONE,
      label: "Jobs",
      link: { to: "/jobs" as Path },
      icon: "business_center" as MaterialNames,
      active: isRouteActive(pathname, "/jobs")
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Photobooth",
      link: { to: "/photobooth" as Path },
      icon: "photo_camera" as MaterialNames,
      active: isRouteActive(pathname, "/photobooth")
    }
  ];
}
